import { ModifiableList } from "@/components/form-items/ModifiableList";
import { UniqueImageFullScreen } from "@/components/modal/UniqueImageFullScreen";
import { BadgeCell } from "@/components/table/CustomCell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  getDefaultImage,
  PublicAttachmentFormat,
  TypeDefaultImage,
} from "@/lib/image";
import { Attachment } from "@/lib/types";
import { useMemo } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type FormHeadingMetadataProps<T extends FieldValues> = {
  reference?: string;
  title?: string;
  subtitle?: string;
  publicationStatus?: string;
  form: UseFormReturn<T>;
  picturesData?: {
    pictures: Attachment[];
    type: TypeDefaultImage;
    size?: PublicAttachmentFormat;
  };
  keyStatus?: string;
  onClick?: () => void;
};

export const FormHeadingMetadata = <T extends FieldValues>({
  reference,
  title,
  subtitle,
  publicationStatus,
  picturesData,
  onClick,
  form,
  keyStatus,
}: FormHeadingMetadataProps<T>) => {
  const { t } = useTranslation();
  const image = useMemo(() => {
    if (!picturesData) return undefined;

    return getDefaultImage(
      picturesData.pictures,
      picturesData.type,
      picturesData.size,
    );
  }, [picturesData]);

  const largeImage = useMemo(() => {
    if (!picturesData) return undefined;

    return getDefaultImage(
      picturesData.pictures,
      picturesData.type,
      "largePublicUrl",
    );
  }, [picturesData]);

  return (
    <div className="max-w-screen-2xl mx-auto p-8 flex justify-between items-start">
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col w-3/4 h-fit gap-4">
          {reference && (
            <p className="text-lg font-bold text-gray-950 text-left">
              {reference}
            </p>
          )}
          {title && (
            <p
              className="text-4xl font-bold text-gray-950 text-left"
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
          {subtitle && (
            <p
              className="text-base  text-gray-950 text-left"
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          )}
          {publicationStatus && (
            <BadgeCell
              value={t(publicationStatus)}
              variant={
                publicationStatus === "published" ? "published" : "draft"
              }
            />
          )}
        </div>
        {keyStatus && (
          <Form {...form}>
            <div className="w-1/4">
              <ModifiableList
                name={keyStatus}
                listKey="liste-status"
                labelClassName="text-primary"
                inputClassName="font-bold"
              />
            </div>
          </Form>
        )}
      </div>
      {picturesData && (
        <div className="flex items-end justify-end w-1/4 relative">
          {image && (
            <UniqueImageFullScreen
              media={{
                url: image,
                largeUrl: largeImage ?? image,
                alt: "",
                className: "w-full max-h-[250px] object-contain object-right",
              }}
            />
          )}
          {!image && (
            <div className="w-full h-[100px] bg-white rounded-lg"></div>
          )}
          <Button
            variant="white"
            className="absolute bottom-1 right-1 text-gray-950 text-sm flex gap-2"
            onClick={onClick}
          >
            {t("formulaireBannierePhotosBouton")}
            <Badge variant="outlined">
              {picturesData?.pictures.length ?? "?"}
            </Badge>
          </Button>
        </div>
      )}
    </div>
  );
};
